import { create } from "zustand";
import { projectMetadata, type MetadataEvent } from "../lib/metadata-history/metadata-events";
import type { ArtMetadata } from "../lib/schedule-presets/art-metadata";
import type { BuilderArtwork } from "../lib/metadata-builder/artwork";
import type { BuilderDayLayouts } from "../lib/metadata-builder/day-layouts";

type MetadataSession = { seed: ArtMetadata; projection: ArtMetadata; artwork: BuilderArtwork | null; dayLayouts: BuilderDayLayouts };

type MetadataHistoryState = {
  activeId: string;
  sessions: Record<string, MetadataSession>;
  initialize: (sessionId: string, seed: ArtMetadata) => void;
  activate: (presetId: string, seed: ArtMetadata) => void;
  project: (events: Record<string, MetadataEvent[]>) => void;
};

// Session seeds and projections live here; the app history owns all events.
export const useMetadataHistoryStore = create<MetadataHistoryState>((set, get) => ({
  activeId: "",
  sessions: {},
  initialize: (sessionId, seed) => {
    const { sessions } = get();
    if (sessions[sessionId]) return;
    set({ sessions: { ...sessions, [sessionId]: { seed, projection: seed, artwork: null, dayLayouts: {} } } });
  },
  activate: (activeId, seed) => {
    const { sessions } = get();
    const session = sessions[activeId] ?? { seed, projection: seed, artwork: null, dayLayouts: {} };
    set({ activeId, sessions: { ...sessions, [activeId]: session } });
  },
  project: events => {
    const sessions = Object.fromEntries(Object.entries(get().sessions).map(([id, session]) => {
      const actions = events[id] ?? [];
      const projection = projectMetadata(session.seed, actions, actions.length);
      const upload = actions.findLast(event => event.type === "artwork/set");
      const artwork = upload?.artwork ?? null;
      const layouts: BuilderDayLayouts = {};
      actions.forEach(event => { if (event.type === "day-layout/set") layouts[event.layout.mode] = event.layout; });
      const layoutsUnchanged = JSON.stringify(layouts) === JSON.stringify(session.dayLayouts);
      const dayLayouts = layoutsUnchanged ? session.dayLayouts : layouts;
      const metadataUnchanged = JSON.stringify(projection) === JSON.stringify(session.projection);
      const unchanged = metadataUnchanged && layoutsUnchanged && artwork === session.artwork;
      const nextProjection = metadataUnchanged ? session.projection : projection;
      return [id, unchanged ? session : { ...session, projection: nextProjection, artwork, dayLayouts }];
    }));
    set({ sessions });
  },
}));
